import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { ArrowLeft, Save } from "lucide-react";
import {
  createSubscriptionPackage,
  updateSubscriptionPackage,
  fetchSubscriptionPackageById,
  resetPackageStatuses,
} from "../../store/slices/subscriptionPackagesSlice";
import { TextField, CheckboxField, TextAreaField } from "../../components/ui/FormControls";
import { showApiError, showApiSuccess } from "../../utils/toast";
import Loader from "../../components/Loader";

const packageSchema = yup.object({
  name: yup
    .string()
    .required("Package name is required")
    .max(255, "Package name must be less than 255 characters"),
  description: yup.string().nullable(),
  status: yup.boolean(),
});

export default function AddSubscriptionPackagePage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const editUuid = searchParams.get("edit");

  const {
    subscriptionPackage,
    packageCreateStatus,
    packageUpdateStatus,
    packageFetchStatus,
    loading,
  } = useSelector((state) => state.subscriptionPackages);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(packageSchema),
    defaultValues: {
      name: "",
      description: "",
      status: true,
    },
  });

  // Fetch package data if editing
  useEffect(() => {
    if (editUuid) {
      dispatch(fetchSubscriptionPackageById(editUuid));
    }
  }, [editUuid, dispatch]);

  // Populate form when package data is loaded
  useEffect(() => {
    if (editUuid && subscriptionPackage && packageFetchStatus === "success") {
      reset({
        name: subscriptionPackage.name || "",
        description: subscriptionPackage.description || "",
        status: Boolean(subscriptionPackage.status),
      });
    }
  }, [subscriptionPackage, packageFetchStatus, editUuid, reset]);

  const onSubmit = async (formData) => {
    try {
      const payload = {
        name: formData.name,
        description: formData.description || null,
        status: formData.status,
      };

      if (editUuid) {
        await dispatch(updateSubscriptionPackage({ uuid: editUuid, data: payload })).unwrap();
        showApiSuccess("Subscription package updated successfully");
      } else {
        await dispatch(createSubscriptionPackage(payload)).unwrap();
        showApiSuccess("Subscription package created successfully");
      }

      navigate("/subscription-packages");
    } catch (error) {
      showApiError(null, error || `Failed to ${editUuid ? "update" : "create"} subscription package`);
    }
  };

  useEffect(() => {
    return () => {
      dispatch(resetPackageStatuses());
    };
  }, [dispatch]);

  const isSubmitting =
    packageCreateStatus === "loading" || packageUpdateStatus === "loading";

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {loading && <Loader />}

      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate("/subscription-packages")}
              className="text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-6 w-6" />
            </button>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                {editUuid ? "Edit Subscription Package" : "Add New Subscription Package"}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {editUuid
                  ? "Update subscription package information"
                  : "Create a new subscription package"}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Package Information
          </h3>

          <div className="space-y-4">
            {/* Package Name */}
            <div>
              <TextField
                disabled={!!editUuid}
                label="Package Name"
                required
                placeholder="Enter package name"
                {...register("name")}
                error={errors?.name?.message}
              />
            </div>

            {/* Description */}
            <div>
              <TextAreaField
                label="Description"
                placeholder="Enter package description (optional)"
                rows={4}
                {...register("description")}
                error={errors?.description?.message}
              />
            </div>

            {/* Status */}
            <div>
              <Controller
                control={control}
                name="status"
                render={({ field }) => (
                  <CheckboxField
                    label="Active Status"
                    checked={Boolean(field.value)}
                    onChange={(checked) => field.onChange(checked)}
                  />
                )}
              />
              <p className="text-sm text-gray-500 mt-1">
                Enable this package to make it available for businesses
              </p>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate("/subscription-packages")}
              className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="h-4 w-4" />
              <span>{isSubmitting ? "Saving..." : editUuid ? "Update Package" : "Create Package"}</span>
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
